import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useI18n, useSelector } from '~Hooks';
import { gql, useQuery } from '@apollo/client';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import { GetInventoryForAllVenues, GetInventoryForAllVenuesVariables } from './__queries__';
import Button from '~Components/Button';

const Content = styled.div`
  padding: 0 48px 24px;
  margin-bottom: auto;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  padding: 16px;
  background-color: #fff;

  div {
    flex: 1;
  }
`;

const Table = styled.div`
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  background-color: #fff;
  height: auto;
  max-height: 330px;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: flex;
  background-color: #fbfbfb;
  align-items: center;
  border-bottom: 1px solid #e8e9ea;
`;

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 8px;
`;

const Row = styled.div`
  background-color: #fff;
  width: 100%;
  display: flex;
  box-sizing: border-box;
  position: relative;
  border: 1px solid;
  border-color: #e8e9ea;
  margin-top: -1px;

  &:nth-of-type(2n) {
    background-color: #fbfbfb;
  }
`;

const Title = styled.div`
  flex: 1;
  color: #41474f;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  padding: 18px 24px;
  box-sizing: border-box;
  text-align: left;
`;

const QtyTitle = styled.div`
  width: 156px;
  text-align: center;
  color: #41474f;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  padding: 18px 8px;
  box-sizing: border-box;
`;

const VenueName = styled.div`
  flex: 1;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.24px;
  color: #1c232c;
  line-height: 20px;
  box-sizing: border-box;
  word-break: break-word;
  text-align: left;
`;

const VenueCount = styled.div`
  padding: 14px 8px;
  width: 156px;
  box-sizing: border-box;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  border-left: 1px solid;
  border-color: #e8e9ea;
  position: relative;
  color: #1c232c;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  transition: border-color 0.2s ease-in-out;
`;

const ALL_VENUES_INVENTORY_QUERY = gql`
  query GetInventoryForAllVenues($companyId: ID!) {
    venues: allVenues(filter: { deleted: false, active: true, company: { id: $companyId } }) {
      id
      name

      inventory {
        snapshot
      }
    }
  }
`;

interface Props {
  itemId: string;
  goBack: () => void;
}

interface InventoryVenue {
  id: string;
  name: string;
  count: number | null;
  outOfStock: boolean | null;
}

const RegionalInventory: React.FunctionComponent<Props> = ({ itemId, goBack }) => {
  const { i18n } = useI18n();
  const companyId = useSelector((state) => state.company);
  const inventory = useInventoryUniverse();

  useEffect(() => {
    refetch();
  }, [inventory]);

  const { data, loading, refetch } = useQuery<
    GetInventoryForAllVenues,
    GetInventoryForAllVenuesVariables
  >(ALL_VENUES_INVENTORY_QUERY, {
    variables: {
      companyId,
    },
  });

  if (loading || !data) {
    return <h2>{i18n('POS.Loading')}</h2>;
  }

  const inventoryForVenue = data.venues.reduce((arr, venue) => {
    const item = venue.inventory?.snapshot?.items.find((item: any) => item.itemId === itemId);

    arr.push({
      id: venue.id,
      name: venue.name,
      count: item ? item.count : null,
      outOfStock: item ? item.outOfStock : null,
    });

    return arr;
  }, [] as InventoryVenue[]);

  return (
    <>
      <Content>
        <Table>
          <TableHeader>
            <Title>{i18n('Inventory.Venue')}</Title>
            <QtyTitle>{i18n('Inventory.Quantity')}</QtyTitle>
          </TableHeader>
          <Body>
            {inventoryForVenue.map((venue) => (
              <Row key={venue.id}>
                <VenueName>{venue.name}</VenueName>
                <VenueCount>
                  {venue.count !== null && venue.count !== 0 && !venue.outOfStock
                    ? venue.count
                    : 0}
                </VenueCount>
              </Row>
            ))}
          </Body>
        </Table>
      </Content>
      <ButtonsWrapper>
        <Button
          handleClick={goBack}
          text={i18n('Inventory.Back')}
          textColor="#2f353d"
          background="transparent"
          border="1px solid #1c232c"
        />
      </ButtonsWrapper>
    </>
  );
};

export default RegionalInventory;
